// Hedefler sayfası — /clients/kpi/hedefler (M16 3. dalga, `kpi_targets`; güvenlik/UX taraması
// 2026-10-08 Codex #4 — eski "TODO-KPI-01" başlığı kapanmış işi anlatıyordu). Set yaprak
// göstergeleri × hedef satırları (`services/kpi/targets.ts`), satır içi hedef düzenleyici, "hedefe
// uzaklık" + ilerleme çubuğu (hesap sunucuda). Danışman aracı (portal'a açılmaz). Satıra tıkla →
// Veri Girişi'nde ?highlight=<code>.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { KpiTargetUpsert, KpiTargetWithProgress } from "@mahir/shared";
import { ApiError } from "../../api/client";
import { deleteKpiTarget, listKpiTargets, upsertKpiTarget } from "../../api/kpi-targets";
import { KpiScreen } from "../../components/kpi/KpiScreen";
import { TargetsTable } from "../../components/kpi/targets/TargetsTable";
import { LoadingState } from "../../components/common/StateSurface";
import { KPI_TARGETS_COPY, targetsStrip } from "../../lib/kpi-targets-ui";
import { readMetricIdParam } from "../../lib/kpi-unit-links";
import { useAuthStore } from "../../stores/auth-store";
import { useKpiStore } from "../../stores/kpi-store";
import { useCreateSetNav } from "./use-create-set-nav";

const C = KPI_TARGETS_COPY.targets;

export function KpiHedeflerPage(): JSX.Element {
  const createSet = useCreateSetNav();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const metricFilter = readMetricIdParam(`?${params.toString()}`);
  const user = useAuthStore((s) => s.user);
  const canEdit = user?.permissions["kpi.edit"] === true;
  const grid = useKpiStore((s) => s.grid);
  const setId = useKpiStore((s) => s.setId);
  const [targets, setTargets] = useState<KpiTargetWithProgress[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    if (!setId) {
      setTargets(null);
      return;
    }
    try {
      setTargets(await listKpiTargets(setId));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : C.loadFailed);
      setTargets([]);
    }
  }, [setId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const allLeaves = useMemo(
    () =>
      grid ? grid.indicators.filter((i) => !grid.indicators.some((c) => c.parentId === i.id)) : [],
    [grid],
  );
  // birim engelinden gelen gösterge süzgeci (eşleşme yoksa süzgeç uygulanmaz — boş tablo olmasın)
  const filtered = metricFilter ? allLeaves.filter((i) => i.metricId === metricFilter) : [];
  const leaves = filtered.length > 0 ? filtered : allLeaves;
  const filterLabel = filtered.length > 0 ? filtered[0]!.label : null;
  const strip = targetsStrip(leaves.length, targets ?? []);

  const onSave = async (body: KpiTargetUpsert): Promise<boolean> => {
    if (!setId) return false;
    setBusy(true);
    setSaveError(null);
    try {
      await upsertKpiTarget(setId, body);
      await reload();
      return true;
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : "Kaydedilemedi");
      return false;
    } finally {
      setBusy(false);
    }
  };
  const onDelete = async (targetId: string): Promise<void> => {
    if (!setId) return;
    setBusy(true);
    try {
      await deleteKpiTarget(setId, targetId);
      await reload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <KpiScreen
      title={KPI_TARGETS_COPY.titles.hedefler}
      showYears={false}
      onCreateSet={() => createSet()}
      onCreateSetForClient={(id) => createSet(id)}
    >
      {!grid || targets === null ? (
        <LoadingState label="Hedefler yükleniyor…" />
      ) : (
        <div className="min-w-0">
          <p className="text-sm mb-3" style={{ color: "var(--color-text-2)", maxWidth: 760 }}>
            {C.subtitle}
          </p>
          <div
            className="flex items-center gap-4 flex-wrap text-sm mb-4 px-3 py-2 rounded-xl"
            style={{
              background: "var(--color-surface)",
              border: "1px solid var(--color-border)",
              color: "var(--color-text-2)",
            }}
            aria-label="Hedef şeridi"
          >
            <span>{C.strip(strip)}</span>
            {filterLabel ? (
              <span>
                {C.filteredBy(filterLabel)}{" "}
                <button
                  type="button"
                  className="underline"
                  style={{ color: "var(--color-primary)" }}
                  onClick={() => {
                    params.delete("metricId");
                    setParams(params, { replace: true });
                  }}
                >
                  {C.clearFilter}
                </button>
              </span>
            ) : null}
            {error ? (
              <span role="alert" style={{ color: "var(--color-error)", marginLeft: "auto" }}>
                {error}
              </span>
            ) : null}
          </div>
          <div
            className="rounded-xl"
            style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
          >
            <TargetsTable
              indicators={leaves}
              treeIndicators={grid.indicators}
              targets={targets}
              entities={grid.entities}
              canEdit={canEdit}
              busy={busy}
              error={saveError}
              onSave={onSave}
              onDelete={onDelete}
              onOpen={(code) =>
                void navigate(`/clients/kpi/tablo?highlight=${encodeURIComponent(code)}`)
              }
            />
          </div>
        </div>
      )}
    </KpiScreen>
  );
}
